import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { FoxSpinnerInline } from '../ui/FoxSpinner';

const ClientStarRanking = ({ kakoId }) => {
  const [currentRanking, setCurrentRanking] = useState([]);
  const [previousRanking, setPreviousRanking] = useState([]);
  const [myPosition, setMyPosition] = useState(null);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [myHistory, setMyHistory] = useState([]);
  const [starRankingConfig, setStarRankingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    if (kakoId) {
      loadAllData();
    }
  }, [kakoId]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadConfig(),
      loadCurrentRanking(),
      loadPreviousRanking(),
      loadMyPosition(),
      loadAvailableRewards(),
      loadMyHistory()
    ]);
    setLoading(false);
  };

  const loadConfig = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/star-ranking/config`);
      setStarRankingConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const loadCurrentRanking = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/star-ranking/current-week`);
      setCurrentRanking(response.data.ranking || []);
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
    }
  };

  const loadPreviousRanking = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/star-ranking/previous-week`);
      setPreviousRanking(response.data.ranking || []);
    } catch (error) {
      console.error('Erro ao carregar ranking anterior:', error);
    }
  };

  const loadMyPosition = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/star-ranking/my-position/${kakoId}`);
      
      // ⚠️ VERIFICAR SE ESTÁ BLOQUEADO (resposta do backend)
      if (response.data.blocked) {
        setIsBlocked(true);
      }
      
      setMyPosition(response.data);
    } catch (error) {
      console.error('Erro ao carregar posição:', error);
    }
  };

  const loadAvailableRewards = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/star-ranking/my-rewards/${kakoId}`);
      setAvailableRewards(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar recompensas:', error);
    }
  };

  const loadMyHistory = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/star-ranking/my-history/${kakoId}`);
      setMyHistory(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const handleClaimReward = async (weekStart) => {
    setClaiming(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/star-ranking/claim-reward`, null, {
        params: {
          show_id: kakoId,
          week_start: weekStart
        }
      });
      
      toast.success(response.data.message);
      await loadAvailableRewards();
      await loadMyHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resgatar bônus');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  // Verificar se o evento está ativo
  if (starRankingConfig && !starRankingConfig.active) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500">
          <p>Evento não está ativo no momento</p>
        </CardContent>
      </Card>
    );
  }

  // MENSAGEM DE BLOQUEIO
  if (isBlocked) {
    return (
      <Card className="border-2 border-red-500">
        <CardContent className="p-6 sm:p-8 text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h3 className="text-xl sm:text-2xl font-bold text-red-600 mb-3">Acesso Bloqueado</h3>
          <p className="text-sm sm:text-base text-gray-700 mb-2">
            Você está bloqueado de participar deste evento por não cumprir requisitos ou descumprir termos e regras.
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mt-4">
            Para mais informações, entre em contato com a administração da agência.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {availableRewards.length > 0 && (
        <Card className="border-2 border-purple-400 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg text-purple-800">⭐ Bônus Disponível!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {availableRewards.map((reward) => (
              <div key={reward.id} className="p-3 sm:p-4 bg-white border-2 border-purple-400 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
                  <div className="flex-1">
                    <p className="font-bold text-sm sm:text-base text-gray-900">
                      {reward.position === 1 && '🥇'} {reward.position === 2 && '🥈'} {reward.position === 3 && '🥉'}
                      {' '}{reward.position}º Lugar
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">Semana {reward.week_start}</p>
                    <p className="text-xs text-gray-500 mt-1">Bônus: {reward.bonus_percentage}%</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-2xl sm:text-3xl font-bold text-purple-600">{reward.bonus_coins.toLocaleString('pt-BR')} 💎</p>
                  </div>
                </div>
                <Button
                  onClick={() => handleClaimReward(reward.week_start)}
                  disabled={claiming}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-sm sm:text-base py-2 sm:py-3"
                >
                  {claiming ? 'Resgatando...' : '⭐ Resgatar Bônus'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card 
        className="text-white overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/hfmxklwr_fundo%20card.png')"
        }}
      >
        <CardHeader className="relative py-4 sm:py-6">
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/qedkwqsn_gggggggggggggg.png" 
              alt="RANKING DE ESTRELAS" 
              className="h-24 sm:h-32 md:h-40 object-contain relative z-10"
            />
          </div>
          <p className="text-xs sm:text-sm text-center text-white drop-shadow-lg mt-2 relative z-10 font-bold">Competição Semanal por Renda</p>
        </CardHeader>
      </Card>

      <Tabs defaultValue="ranking-atual" className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-auto">
          <TabsTrigger value="ranking-atual" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm py-2 px-1 sm:px-3">
            <span className="hidden sm:inline">📊 Ranking Atual</span>
            <span className="sm:hidden">📊 Atual</span>
          </TabsTrigger>
          <TabsTrigger value="semana-anterior" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm py-2 px-1 sm:px-3">
            <span className="hidden sm:inline">📅 Semana Anterior</span>
            <span className="sm:hidden">📅 Anterior</span>
          </TabsTrigger>
          <TabsTrigger value="historico" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm py-2 px-1 sm:px-3">
            <span className="hidden sm:inline">📜 Meu Histórico</span>
            <span className="sm:hidden">📜 Histórico</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ranking-atual" className="mt-4 space-y-4">
          <Card 
            className="overflow-hidden bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/rxd3o0ty_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2018_06_53.png')"
            }}
          >
            <CardHeader className="relative bg-white/80 backdrop-blur-sm">
              <CardTitle className="text-base sm:text-lg">Ranking da Semana Atual</CardTitle>
              {myPosition && myPosition.my_position && (
                <p className="text-sm text-gray-600 mt-1 font-bold">Você está em {myPosition.my_position}º lugar com {myPosition.my_income.toLocaleString('pt-BR')} 💎</p>
              )}
            </CardHeader>
            <CardContent className="relative bg-white/90 backdrop-blur-sm">
              <div className="space-y-3">
                {currentRanking.slice(0, 10).map((host, idx) => {
                  const prizeAmount = starRankingConfig && idx < 3 ? Math.floor(host.anchor_income * (idx === 0 ? starRankingConfig.top1_percentage : idx === 1 ? starRankingConfig.top2_percentage : starRankingConfig.top3_percentage) / 100) : 0;

                  if (idx < 3) {
                    return (
                      <Card key={host.show_id} className={`${idx === 0 ? 'border-2 border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50' : idx === 1 ? 'border-2 border-gray-400 bg-gradient-to-br from-gray-50 to-slate-100' : 'border-2 border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50'}`}>
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                              <span className="text-3xl sm:text-4xl flex-shrink-0">{idx === 0 && '🥇'}{idx === 1 && '🥈'}{idx === 2 && '🥉'}</span>
                              <img src={host.avatar} alt={host.nickname} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 sm:border-4 border-white shadow-lg flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                                  <p className="font-bold text-sm sm:text-base lg:text-lg truncate">{host.nickname}</p>
                                  {host.show_id === kakoId && <Badge className="bg-purple-600 text-xs">VOCÊ</Badge>}
                                </div>
                                <div className="mt-1">
                                  <p className="text-xs text-gray-500">Prêmio:</p>
                                  <p className="text-base sm:text-lg lg:text-xl font-bold text-purple-700">+{prizeAmount.toLocaleString('pt-BR')} 🪙</p>
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">{host.anchor_income.toLocaleString('pt-BR')} 💎</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  } else {
                    return (
                      <div key={host.show_id} className={`p-2 sm:p-3 border rounded flex items-center justify-between gap-2 ${host.show_id === kakoId ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-purple-500 border-2' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                          <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-600 w-6 sm:w-8 flex-shrink-0">{idx + 1}º</span>
                          <img src={host.avatar} alt={host.nickname} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0" />
                          <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
                            <p className="font-semibold text-xs sm:text-sm truncate">{host.nickname}</p>
                            {host.show_id === kakoId && <Badge className="bg-purple-600 text-xs flex-shrink-0">VOCÊ</Badge>}
                          </div>
                        </div>
                        <p className="font-mono font-bold text-purple-700 text-xs sm:text-sm whitespace-nowrap flex-shrink-0">{host.anchor_income.toLocaleString('pt-BR')} 💎</p>
                      </div>
                    );
                  }
                })}
              </div>
              
              {myPosition && myPosition.my_position > 10 && (
                <div className="mt-4 p-3 sm:p-4 bg-purple-50 border-2 border-purple-400 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs sm:text-sm text-purple-600">Sua Posição</p>
                      <p className="text-xl sm:text-2xl font-bold text-purple-700">{myPosition.my_position}º lugar</p>
                      <p className="text-xs text-gray-500 mt-1">Sua renda: {myPosition.my_income.toLocaleString('pt-BR')} 💎</p>
                    </div>
                    <div className="text-left sm:text-right">
                      {currentRanking[9] && (
                        <>
                          <p className="text-xs sm:text-sm text-purple-600">Para entrar no Top 10:</p>
                          <p className="text-base sm:text-lg font-bold text-orange-600">{(currentRanking[9].anchor_income - myPosition.my_income + 1).toLocaleString('pt-BR')} 💎</p>
                          <p className="text-xs text-gray-500">a mais que o 10º</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="semana-anterior" className="mt-4">
          <Card className="overflow-hidden bg-cover bg-center bg-no-repeat" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/rxd3o0ty_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2018_06_53.png')"}}>
            <CardHeader className="relative bg-white/80 backdrop-blur-sm pb-3">
              <CardTitle className="text-sm sm:text-base lg:text-lg">Ranking da Semana Anterior</CardTitle>
            </CardHeader>
            <CardContent className="relative bg-white/90 backdrop-blur-sm">
              {previousRanking.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">Sem dados da semana anterior</p>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {previousRanking.slice(0, 10).map((host, idx) => (
                    <div key={host.show_id} className={`p-2 sm:p-3 border rounded flex items-center justify-between gap-2 ${idx < 3 ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'} ${host.show_id === kakoId ? 'border-purple-500 border-2' : ''}`}>
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <span className="text-base sm:text-lg lg:text-xl font-bold flex-shrink-0">{idx === 0 && '🥇'}{idx === 1 && '🥈'}{idx === 2 && '🥉'}{idx > 2 && `${idx + 1}º`}</span>
                        <p className="font-semibold text-xs sm:text-sm truncate">{host.nickname || host.show_id}</p>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                        <p className="font-mono font-bold text-purple-700 text-xs sm:text-sm whitespace-nowrap">{host.anchor_income.toLocaleString('pt-BR')} 💎</p>
                        {host.is_top3 && <Badge className="bg-purple-600 text-xs">+{host.bonus_coins.toLocaleString('pt-BR')} 🪙</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico" className="mt-4">
          <Card className="overflow-hidden bg-cover bg-center bg-no-repeat" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_7f473fba-bcad-429d-a8e1-d860614a1332/artifacts/rxd3o0ty_ChatGPT%20Image%205%20de%20fev.%20de%202026%2C%2018_06_53.png')"}}>
            <CardHeader className="relative bg-white/80 backdrop-blur-sm pb-3">
              <CardTitle className="text-sm sm:text-base lg:text-lg">📜 Meu Histórico de Bônus</CardTitle>
            </CardHeader>
            <CardContent className="relative bg-white/90 backdrop-blur-sm">
              {myHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">Você ainda não resgatou nenhum bônus</p>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {myHistory.map((reward) => (
                    <div key={reward.id} className="p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-300 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                        <div className="flex-1">
                          <p className="font-bold text-sm sm:text-base text-gray-900">{reward.position === 1 && '🥇'} {reward.position === 2 && '🥈'} {reward.position === 3 && '🥉'} {reward.position}º Lugar</p>
                          <p className="text-xs sm:text-sm text-gray-600">Semana: {reward.week_start} a {reward.week_end}</p>
                          <p className="text-xs text-gray-500 mt-1">Resgatado: {new Date(reward.claimed_at).toLocaleDateString('pt-BR')}</p>
                        </div>
                        <div className="text-left sm:text-right">
                          <p className="text-xl sm:text-2xl font-bold text-purple-600">{reward.bonus_coins.toLocaleString('pt-BR')} 💎</p>
                          <p className="text-xs text-gray-500">{reward.bonus_percentage}% bônus</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClientStarRanking;
